import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Shield,
  Lock,
  CheckCircle,
  AlertCircle,
  X,
  Eye,
  EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';

const AccountSettingsPage: React.FC = () => {
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();

  // Messages
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Change password form — no current password field. The user is already
  // authenticated (JWT + MFA at login), so no re-challenge is required.
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Cancel any in-progress form state and clear messages. Wired to the
  // page-level Cancel button next to the Change Password submit button.
  const handleCancel = () => {
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setError(null);
    setSuccess(null);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    try {
      setPasswordLoading(true);
      setError(null);
      await changePassword(newPassword);
      setSuccess('Password changed successfully');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <Header
        title="Account Settings"
        showBackButton={true}
        onBack={() => navigate('/dashboard')}
      />

      <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        {/* Success/Error Messages */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '1rem',
              background: '#10b981',
              color: 'white',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <CheckCircle size={20} />
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: '1rem',
              background: '#ef4444',
              color: 'white',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <AlertCircle size={20} />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </motion.div>
        )}

        {/* User Info Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <User size={24} style={{ color: '#3b82f6' }} />
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Profile Information</h2>
          </div>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Name</label>
              <div style={{ fontSize: '1rem', fontWeight: '500', color: '#1f2937' }}>{user?.name || 'N/A'}</div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Email</label>
              <div style={{ fontSize: '1rem', fontWeight: '500', color: '#1f2937' }}>{user?.email || 'N/A'}</div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.25rem' }}>Role</label>
              <span style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600',
                background: user?.role?.toLowerCase() === 'admin' ? '#ef444420' : '#3b82f620',
                color: user?.role?.toLowerCase() === 'admin' ? '#ef4444' : '#3b82f6'
              }}>
                {user?.role?.toUpperCase() || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <Shield size={24} style={{ color: '#3b82f6' }} />
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Security</h2>
          </div>

          {/* Change Password */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Lock size={18} style={{ color: '#6b7280' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Change Password</h3>
            </div>
            <form onSubmit={handleChangePassword}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.9rem' }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <motion.button
                  type="submit"
                  disabled={passwordLoading || !newPassword || !confirmPassword}
                  whileHover={{ scale: passwordLoading ? 1 : 1.02 }}
                  whileTap={{ scale: passwordLoading ? 1 : 0.98 }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '6px',
                    background: '#3b82f6',
                    color: 'white',
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    opacity: passwordLoading || !newPassword || !confirmPassword ? 0.6 : 1
                  }}
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleCancel}
                  disabled={passwordLoading}
                  whileHover={{ scale: passwordLoading ? 1 : 1.02 }}
                  whileTap={{ scale: passwordLoading ? 1 : 0.98 }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: 'white',
                    color: '#374151',
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </motion.button>
              </div>
            </form>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '2rem 0' }}></div>

          {/* MFA Status */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Shield size={18} style={{ color: '#6b7280' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Multi-Factor Authentication</h3>
            </div>
            <div style={{
              padding: '1rem',
              background: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <CheckCircle size={20} style={{ color: '#10b981' }} />
              <span style={{ fontSize: '0.9rem', color: '#166534' }}>
                MFA is enabled for your account. Manage MFA settings from the login page.
              </span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
